import { loadGrant, renew, storeGrant, type Naming, type Session } from "./auth.ts";
import { onLogoutElsewhere } from "./auth-channel.ts";
import type { LoadingEvent } from "./loading-state.ts";
import { showLogin, type HeldToken, type Returning } from "./login.tsx";
import type { Ask } from "./ask.ts";
import type { ConfirmLogoutEverywhere } from "./logout-everywhere.tsx";
import type { RetryLogout } from "./logout-retry.tsx";
import type { EntryLooks } from "./look-step.tsx";
import { resume } from "./resume.ts";
import { StatusPoll, probeStatus } from "./status-poll.ts";

/**
 * How an account's tab is admitted to play (P1.4-T2, P1.4-T6, P1.4-T18, P1.4-T7): the tab's stored grant while
 * its token lasts, else a refresh of it naming the character it plays, else, in a tab that holds no grant, a
 * resume of this browser's kept session, which opens the login screen on the returning prompt, else the log in
 * form. Only the gateway's refusal of a refresh forgets the stored grant: a refresh that got no answer keeps it,
 * so a reload once the gateway is back signs in again (ADR-075 decision 9). A tab another tab logged out of
 * learns of it here too: when the signal comes, it asks the gateway about the session it holds and, refused,
 * forgets it and reloads, which shows the log in form or the prompt for a keep that still stands. While the
 * tab is being admitted it probes the gateway's status, so the entry screens say whether the server is offline or
 * under maintenance before a login is tried (ADR-078 decision 3); the probe stops once the tab is admitted.
 */

/**
 * The loading shell as admission uses it: the sign-in step's progress, and stepping aside while the login
 * screen is up.
 */
export interface AdmissionShell {
  readonly dispatch: (event: LoadingEvent) => void;
  readonly hide: () => void;
  readonly show: () => void;
}

/**
 * The dialogs the login screen offers: the confirmation before logging out everywhere, the page's confirm dialog,
 * and the dialog for a logout that did not finish; and the looks its Look step and character select draw.
 */
export interface AdmissionDialogs {
  readonly confirmEverywhere: ConfirmLogoutEverywhere;
  readonly ask: Ask;
  readonly retryLogout: RetryLogout;
  readonly looks: EntryLooks;
}

/**
 * Admits the tab's account: the session to play with, once there is one.
 *
 * @param base The account origin.
 * @param shell The loading shell.
 * @param dialogs The dialogs the login screen offers.
 * @param held Where the login screen writes the refresh token it holds while it is up.
 * @param status The server's status poll the entry screens show, a probe of `base` unless given.
 * @returns The session, which is stored in the tab.
 */
export async function admitAccount(base: string, shell: AdmissionShell, dialogs: AdmissionDialogs, held: HeldToken, status: StatusPoll = new StatusPoll({ probe: () => probeStatus(base) })): Promise<Session> {
  const stored = loadGrant();
  if (stored?.kind === "playing" && stored.session.expiresAt > Date.now()) {
    return stored.session;
  }
  status.start();
  try {
    return await admitWithScreens(base, shell, dialogs, held, stored, status);
  } finally {
    status.stop();
  }
}

/**
 * Admits a tab whose stored grant cannot be played at once: a refresh, a resume, or the login screen.
 *
 * @param base The account origin.
 * @param shell The loading shell.
 * @param dialogs The login screen's dialogs.
 * @param held Where the login screen writes the refresh token it holds.
 * @param stored The tab's stored grant, or null.
 * @param status The server's status poll, running.
 * @returns The session, which is stored in the tab.
 */
async function admitWithScreens(base: string, shell: AdmissionShell, dialogs: AdmissionDialogs, held: HeldToken, stored: ReturnType<typeof loadGrant>, status: StatusPoll): Promise<Session> {
  shell.dispatch({ type: "started", step: "sign-in", total: 1 });
  let pending: Naming | null = null;
  let returning: Returning | null = null;
  const attempt = stored === null ? null : stored.kind === "playing" ? await renew(base, stored.session.refreshToken, stored.session.characterId) : await renew(base, stored.naming.refreshToken);
  if (attempt?.kind === "granted") {
    storeGrant(attempt.grant);
    if (attempt.grant.kind === "playing") {
      shell.dispatch({ type: "finished", step: "sign-in" });
      return attempt.grant.session;
    }
    pending = attempt.grant.naming;
  } else if (attempt?.kind === "refused") {
    storeGrant(null);
  }
  if (attempt === null || attempt.kind === "refused") {
    const resumed = await resume(base);
    returning = resumed.kind === "granted" ? { grant: resumed.grant, email: resumed.email } : null;
  }
  shell.hide();
  const session = await showLogin(base, { pending, returning, confirmEverywhere: dialogs.confirmEverywhere, ask: dialogs.ask, held, retryLogout: dialogs.retryLogout, status, looks: dialogs.looks });
  shell.show();
  storeGrant({ kind: "playing", session });
  shell.dispatch({ type: "finished", step: "sign-in" });
  return session;
}

/**
 * Listens for another tab's logout and checks the session this tab holds with the gateway: refused, the tab
 * forgets its grant and reloads; anything else, or no session held, changes nothing.
 *
 * @param base The account origin.
 * @param held The refresh token this tab holds: the login screen's while it is up, then the playing session's.
 * @param leave What the tab does once its session is gone, which the page gives as a reload.
 * @returns A function that stops listening.
 */
export function watchLogoutElsewhere(base: string, held: HeldToken, leave: () => void): () => void {
  return onLogoutElsewhere(() => {
    const token = held.refreshToken;
    if (token === null) {
      return;
    }
    void renew(base, token).then((attempt) => {
      if (attempt.kind === "refused" && held.refreshToken === token) {
        storeGrant(null);
        leave();
      }
    });
  });
}
